import { mstCommon } from '@libs/domain';
import type { Characters, Item, PageProps } from '@ps-smart-cp/ppfw-client';
import {
  DatePickerField,
  getNameWithDefault,
  LabelField,
  Template,
  TextStringField,
  useGuideTab,
  useLocalizedContents,
  useTry,
  useValidator,
} from '@ps-smart-cp/ppfw-client';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { MstRegistrationFooterButtonGroup } from '../components';
import { mstRegistrationFieldConfig } from '../item-config';
import type { MstRegistration } from '../models';
import type { FetchFunction, RegistrationFunction, UpdateFunction } from '../repositories';
import type { BaseMstTmplProps } from './MstTmpl.type';

interface MstRegistrationState<T extends MstRegistration> {
  mst: T;
}

interface MstRegistrationTmplCommonProps<T extends MstRegistration> extends BaseMstTmplProps<T> {
  children?: (mst: T, setMst: (t: T) => void, errorFields: string[], fetching: boolean) => ReactNode;
}

type RequestType<T extends MstRegistration = never> =
  | { type: 'modify'; updateFunction: UpdateFunction<T>; fetchFunction: FetchFunction<T> }
  | { type: 'new'; registrationFunction: RegistrationFunction<T> };

type MstRegistrationTmplProps<T extends MstRegistration> = MstRegistrationTmplCommonProps<T> & RequestType<T>;

function MstRegistrationTmpl<T extends MstRegistration>({
  mstName,
  initMst,
  items,
  options,
  children,
  rerender,
  rerenderContentsOnly,
  ...restProps
}: MstRegistrationTmplProps<T> & PageProps) {
  const { id } = useParams();
  const location = useLocation();

  const { errorFields, tryRequest, validateThenTryRequest } = useTry();
  const { msg, locale } = useLocalizedContents();
  const validator = useValidator();
  const { setInfo } = useGuideTab();

  const [mst, setMst] = useState(() =>
    location.state && restProps.type === 'new' ? (location.state as MstRegistrationState<T>).mst : initMst(),
  );
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    const fetchData = () => {
      tryRequest(async () => {
        if (restProps.type === 'new' || !id) {
          return;
        }

        setMst(await restProps.fetchFunction({ id: +id }));
        setFetching(!fetching);
      });
    };

    void fetchData();
  }, []);

  const onRegister = validateThenTryRequest(
    () => validator.with(mst, items, options).execute(),
    async () => {
      if (restProps.type === 'new') {
        await restProps.registrationFunction(mst);
        setInfo([msg.ico13002]);
        rerenderContentsOnly();
      } else if (id) {
        await restProps.updateFunction(+id, mst);
        setInfo([msg.ico13002]);
      }
    },
  );

  return (
    <Template
      title={
        getNameWithDefault(mstName.displayName, locale) +
        getNameWithDefault(
          restProps.type === 'new'
            ? mstCommon.titleNames.registration.displayName
            : mstCommon.titleNames.modify.displayName,
          locale,
        )
      }
      footerButtonGroup={<MstRegistrationFooterButtonGroup onRegister={onRegister} onReset={rerender} />}
    >
      {restProps.type === 'new' ? (
        <TextStringField
          items={{ code: items.code as Item<Characters> }}
          obj={mst}
          setObj={setMst}
          prop="code"
          errorFields={errorFields}
        />
      ) : (
        <LabelField itemName={mstRegistrationFieldConfig.code} value={mst.code} bgColor="white" />
      )}
      <TextStringField
        items={mstRegistrationFieldConfig}
        obj={mst}
        setObj={setMst}
        prop="name"
        errorFields={errorFields}
      />
      <TextStringField
        items={mstRegistrationFieldConfig}
        obj={mst}
        setObj={setMst}
        prop="mstSpareName01"
        errorFields={errorFields}
      />
      <TextStringField
        items={mstRegistrationFieldConfig}
        obj={mst}
        setObj={setMst}
        prop="mstSpareName02"
        errorFields={errorFields}
      />
      <DatePickerField
        items={mstRegistrationFieldConfig}
        componentType="ymd"
        obj={mst}
        setObj={setMst}
        prop="validityStartDate"
        errorFields={errorFields}
      />
      <DatePickerField
        items={mstRegistrationFieldConfig}
        componentType="ymd"
        obj={mst}
        setObj={setMst}
        prop="validityEndDate"
        errorFields={errorFields}
      />
      <TextStringField
        items={mstRegistrationFieldConfig}
        obj={mst}
        setObj={setMst}
        prop="mstNote"
        errorFields={errorFields}
      />
      {children?.(mst, setMst, errorFields, fetching)}
    </Template>
  );
}

export type { MstRegistrationState, MstRegistrationTmplCommonProps, MstRegistrationTmplProps, RequestType };
export { MstRegistrationTmpl };
